/**
 * Centralised pickers for Project Name and Client (Company) fields.
 *
 *   - <CompanyCombobox>      → Search existing CRM companies OR keep free-text.
 *                              Used for "Client" / "Client Name" anywhere the
 *                              user might type a brand-new client.
 *
 *   - <ProjectCombobox>      → Search past opportunity project names OR keep
 *                              free-text. Used by the BD opportunity form.
 *
 * Both return a plain string (`name`) to the caller so they slot into
 * existing free-text bindings without schema changes.
 *
 * (Two other flavours were removed in the RMC simplification:
 * <ProjectStrictPicker> — strict pick from ACTIVE projects only, no
 * free-text — was permanently unusable once the Project entity was removed
 * (empty options, no escape hatch; one consumer was even a hard-required
 * field, so no fuel tank could ever be created — see fuel_management.py's
 * TankIn.project_id fix). <CategoryMultiCombobox>/<NatureCombobox> and this
 * component's own "source='enquiries'" mode were built for the tendering
 * workspace (EnquiryDetail.jsx) — deleted entirely once that whole feature
 * was confirmed to have zero backend behind it and RMC has no tendering
 * process. Same for loadProjects()/loadEnquiryNames() below, both of which
 * called endpoints (/projects, /enquiries) that no longer exist.)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, ChevronDown, Plus } from 'lucide-react';
import { apiGet } from '../../lib/api';

// ── Module-level caches so a page doesn't re-fetch on every modal open ──
let _companyCache = null;
let _oppNamesCache = null;

export const refreshPickerCaches = () => {
  _companyCache = null;
  _oppNamesCache = null;
};

async function loadCompanies() {
  if (_companyCache) return _companyCache;
  try {
    const r = await apiGet('/crm/companies?scope=all');
    _companyCache = (r?.companies || []).map((c) => ({ id: c.id, name: c.name }));
  } catch { _companyCache = []; }
  return _companyCache;
}

async function loadOpportunityNames() {
  if (_oppNamesCache) return _oppNamesCache;
  try {
    const r = await apiGet('/crm/opportunities');
    const list = r?.opportunities || r?.items || (Array.isArray(r) ? r : []);
    _oppNamesCache = Array.from(new Set(list.map((o) => o.project_name).filter(Boolean)));
  } catch { _oppNamesCache = []; }
  return _oppNamesCache;
}

// ─────────────────────────────────────────────────────────────────────
// Shared combobox primitive — type to filter, click to pick, free-text
// fallback if `allowFreeText` is true.
// ─────────────────────────────────────────────────────────────────────
function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  allowFreeText = false,
  testid,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // When the dropdown is closed, the display value mirrors `value`.
  // When open, the user is typing into `query`.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return options.slice(0, 100);
    return options.filter((o) => (o.label || '').toLowerCase().includes(q)).slice(0, 100);
  }, [options, q]);

  const exactMatch = useMemo(
    () => options.find((o) => (o.label || '').toLowerCase() === q),
    [options, q]
  );

  const pick = (label) => {
    onChange(label || '');
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-sm text-sm bg-white text-left hover:border-[#1F7A6E] ${
          open ? 'border-[#1F7A6E] ring-1 ring-[#1F7A6E]' : 'border-slate-300'
        } ${required && !value ? 'border-rose-300' : ''}`}
        data-testid={testid}
      >
        <span className={value ? 'text-slate-900 truncate' : 'text-slate-400 truncate'}>
          {value || placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="p-0.5 hover:bg-slate-200 rounded-sm"
              aria-label="Clear"
            >
              <X size={12} className="text-slate-400" />
            </span>
          )}
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden">
          <div className="border-b border-slate-200 px-2.5 py-2 flex items-center gap-1.5 bg-slate-50">
            <Search size={12} className="text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-sm outline-none"
              data-testid={testid ? `${testid}-input` : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filtered.length) pick(filtered[0].label);
                  else if (allowFreeText && query.trim()) pick(query.trim());
                }
              }}
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-700">
                <X size={12} />
              </button>
            )}
          </div>
          <ul className="max-h-64 overflow-y-auto py-1 text-sm">
            {allowFreeText && query.trim() && !exactMatch && (
              <li>
                <button
                  type="button"
                  onClick={() => pick(query.trim())}
                  className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 border-b border-slate-100"
                  data-testid={testid ? `${testid}-add-new` : undefined}
                >
                  <Plus size={12} />
                  <span>Add new: <strong>{query.trim()}</strong></span>
                </button>
              </li>
            )}
            {filtered.length === 0 && !(allowFreeText && query.trim()) ? (
              <li className="px-3 py-4 text-center text-xs text-slate-400">
                {q ? `No matches for "${query}"` : 'No options'}
              </li>
            ) : (
              filtered.map((o) => (
                <li key={o.key}>
                  <button
                    type="button"
                    onClick={() => pick(o.label)}
                    className={`w-full text-left px-3 py-2 hover:bg-teal-50 ${
                      o.label === value ? 'bg-teal-50/60 font-semibold text-[#1F7A6E]' : 'text-slate-700'
                    }`}
                  >
                    {o.label}
                    {o.hint && <span className="text-[10px] text-slate-400 ml-1">· {o.hint}</span>}
                  </button>
                </li>
              ))
            )}
          </ul>
          {options.length > 100 && !q && (
            <div className="px-3 py-1.5 border-t border-slate-200 text-[10px] text-slate-400 bg-slate-50">
              Showing first 100 of {options.length}. Type to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// CompanyCombobox — Client field, backed by CRM Companies master.
// ─────────────────────────────────────────────────────────────────────
export function CompanyCombobox({ value, onChange, placeholder = 'Search clients (companies)…', testid = 'company-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    loadCompanies().then((cos) => setOpts(cos.map((c) => ({ key: c.id, label: c.name }))));
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// ProjectCombobox — project-name field. Suggestions come from past
// opportunity project names; free-text always allowed since a new
// enquiry's project may not have a prior record yet.
// ─────────────────────────────────────────────────────────────────────
export function ProjectCombobox({ value, onChange, placeholder = 'Search projects / past opportunities…', testid = 'project-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadOpportunityNames().then((opp) => {
      if (cancelled) return;
      const seen = new Set();
      const out = [];
      opp.forEach((n) => {
        if (!n || seen.has(n.toLowerCase())) return;
        seen.add(n.toLowerCase());
        out.push({ key: `opp-${n}`, label: n, hint: 'Past opportunity' });
      });
      setOpts(out);
    });
    return () => { cancelled = true; };
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// LocationCombobox — single Location field for the BD opportunity form.
// Search/select from real usage history (sourced from
// /masters/locations/suggest) OR add a brand-new location (free-text).
// The list grows organically as BD enters locations at the source.
// ─────────────────────────────────────────────────────────────────────
async function loadLocations() {
  try {
    const r = await apiGet('/masters/locations/suggest');
    return r?.items || [];
  } catch { return []; }
}

export function LocationCombobox({ value, onChange, placeholder = 'Search or add a location…', testid = 'location-combobox', required = false }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    loadLocations().then((locs) => setOpts(locs.map((l) => ({ key: l, label: l }))));
  }, []);
  return (
    <Combobox
      value={value}
      onChange={onChange}
      options={opts}
      placeholder={placeholder}
      allowFreeText={true}
      testid={testid}
      required={required}
    />
  );
}
